// Frontend auth utilities — all localStorage + API calls, safe to import in client components
import type { UserGardenLocation } from '@/lib/userGardenLocation';
import type { CombinedTaskData } from '@/lib/dataSync';
import type { HarvestEntry } from '@/lib/harvestLog';
import type { PlantingLog } from '@/lib/plantingTracker';
import type { JournalEntry } from '@/data/journal';
import type { SeedPacket } from '@/lib/seedInventory';

const TOKEN_KEY = 'garden-auth-token';

// ── Token storage ─────────────────────────────────────────────────────────────

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function clearStoredUserData(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('garden-journal-entries');
  localStorage.removeItem('garden-journal-entries-prev');
  localStorage.removeItem('garden-plans');
  localStorage.removeItem('garden-user-zone');
  localStorage.removeItem('garden-zone');
  localStorage.removeItem('garden-zone-zip');
  localStorage.removeItem('garden-user-location');
  localStorage.removeItem('garden-almanac');
  localStorage.removeItem('garden_favorites');
  localStorage.removeItem('garden-active-plan');
  localStorage.removeItem('garden-active-tab');
  localStorage.removeItem('garden-calendar-tab');
  localStorage.removeItem('garden-planting-logs');
  localStorage.removeItem('garden-planting-logs-prev');
  localStorage.removeItem('garden-auto-task-state');
  localStorage.removeItem('garden-task-recurrence-v1');
  localStorage.removeItem('garden-guidance-shown-v1');
  localStorage.removeItem('garden-planting-timing-dismissed');
  localStorage.removeItem('garden-harvest-log');
  localStorage.removeItem('garden-harvest-log-prev');
  localStorage.removeItem('garden-seed-inventory');
  localStorage.removeItem('garden-tasks');
  localStorage.removeItem('garden-weather-cache');
  localStorage.removeItem('garden-weather-cache-v3');
  localStorage.removeItem('garden-planned-plants');
  Object.keys(localStorage)
    .filter(k => k.startsWith('garden-tasks') || k.startsWith('garden-custom-tasks'))
    .forEach(k => localStorage.removeItem(k));
}

// ── User type ─────────────────────────────────────────────────────────────────

export interface AuthUser {
  email: string;
  name: string;
  tier: 'free' | 'pro';
  zone: number | null;
  zip?: string | null;
  gardenLocation?: UserGardenLocation | null;
  trialStarted: string | null;
  trialEnded: boolean;
  onboardingComplete: boolean;
  motivations: string[];
  experienceLevel: 'beginner' | 'intermediate' | 'experienced' | null;
  proOverride?: ProOverride;
  plan?: 'monthly' | 'annual' | null;
  subscriptionStatus?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean | null;
}

export interface ProOverride {
  grantedBy: string;
  grantedAt: string;
  expiresAt: string;
  reason?: string;
}

export interface UserBootstrapData {
  tasks: CombinedTaskData;
  journalEntries: JournalEntry[];
  plantingLogs: PlantingLog[];
  harvestEntries: HarvestEntry[];
  seedInventory: SeedPacket[];
  plannedPlants: string[];
  favorites: string[];
}

export interface UserBootstrap {
  user: AuthUser;
  data: UserBootstrapData;
}

export function isAdmin(user: { email?: string | null } | null | undefined): boolean {
  return (
    user?.email === 'whitney@willowbottom.com' ||
    user?.email === 'rsvalencia@gmail.com'
  );
}

// ── Tier helpers (client-safe, pure functions) ────────────────────────────────

export function hasActiveProOverride(user: AuthUser): boolean {
  const expiresAt = user.proOverride?.expiresAt
    ? new Date(user.proOverride.expiresAt).getTime()
    : NaN;
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

export function isTrialActive(user: AuthUser): boolean {
  if (!user.trialStarted || user.trialEnded) return false;
  const daysSince = (Date.now() - new Date(user.trialStarted).getTime()) / 86_400_000;
  return daysSince < 7;
}

export function isProUser(user: AuthUser): boolean {
  // Admin accounts have full Pro access across gated surfaces.
  if (isAdmin(user)) return true;
  if (user.tier === 'pro') return true;
  if (hasActiveProOverride(user)) return true;
  return isTrialActive(user);
}

export function getTrialDaysRemaining(user: AuthUser): number {
  if (!user.trialStarted || user.trialEnded) return 0;
  const daysSince = (Date.now() - new Date(user.trialStarted).getTime()) / 86_400_000;
  return Math.max(0, Math.ceil(7 - daysSince));
}

export function hasUsedTrial(user: AuthUser): boolean {
  return user.trialStarted !== null;
}

// ── Auth actions ──────────────────────────────────────────────────────────────

export async function register(
  email: string,
  password: string,
  name: string,
): Promise<{ success: boolean; user?: AuthUser; error?: string }> {
  try {
    const res = await fetch('/api/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json() as { success?: boolean; token?: string; user?: AuthUser; error?: string };
    if (data.token) setToken(data.token);
    return data.success
      ? { success: true, user: data.user }
      : { success: false, error: data.error ?? 'Registration failed.' };
  } catch {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
}

export async function login(
  email: string,
  password: string,
): Promise<{ success: boolean; user?: AuthUser; error?: string }> {
  try {
    const res = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json() as { success?: boolean; token?: string; user?: AuthUser; error?: string };
    if (data.token) setToken(data.token);
    return data.success
      ? { success: true, user: data.user }
      : { success: false, error: data.error ?? 'Login failed.' };
  } catch {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
}

export function logout(): void {
  clearToken();
  clearStoredUserData();
}

export class AuthWriteError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AuthWriteError';
    this.status = status;
  }
}

function dispatchClientEvent(name: string, detail?: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

async function getResponseErrorMessage(response: Response): Promise<string | null> {
  try {
    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
      const data = await response.json() as { error?: unknown; message?: unknown };
      if (typeof data.error === 'string' && data.error.trim()) return data.error;
      if (typeof data.message === 'string' && data.message.trim()) return data.message;
      return null;
    }

    const text = (await response.text()).trim();
    return text || null;
  } catch {
    return null;
  }
}

export async function getUser(): Promise<AuthUser | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch('/api/validate-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await res.json() as { success?: boolean; user?: AuthUser };
    if (!data.success) {
      clearToken();
      return null;
    }
    return data.user ?? null;
  } catch {
    return null;
  }
}

export async function getBootstrap(): Promise<UserBootstrap | null> {
  const token = getToken();
  if (!token) return null;
  try {
    const res = await fetch('/api/user/bootstrap', {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status === 401 || res.status === 404) {
      clearToken();
      return null;
    }
    if (!res.ok) return null;

    const data = await res.json() as {
      success?: boolean;
      user?: AuthUser;
      data?: UserBootstrapData;
    };
    if (!data.success || !data.user || !data.data) {
      clearToken();
      return null;
    }

    return {
      user: data.user,
      data: data.data,
    };
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  return getToken() !== null;
}

export async function forgotPassword(
  email: string,
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await fetch('/api/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await res.json() as { success?: boolean; message?: string; error?: string };
    return data.success
      ? { success: true, message: data.message }
      : { success: false, error: data.error ?? 'Request failed.' };
  } catch {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch('/api/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    const data = await res.json() as { success?: boolean; error?: string };
    return data.success
      ? { success: true }
      : { success: false, error: data.error ?? 'Password reset failed.' };
  } catch {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
}

// ── Authenticated fetch helper ────────────────────────────────────────────────

/** Wraps fetch with the stored Bearer token in the Authorization header. */
export function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  headers.set('Content-Type', 'application/json');
  return fetch(url, { ...init, headers });
}

export async function authWrite(
  url: string,
  init: RequestInit = {},
  fallbackMessage = 'Your changes were not synced. Please try again.',
): Promise<Response> {
  try {
    const response = await authFetch(url, init);

    if (response.status === 401) {
      clearToken();
      dispatchClientEvent('garden:auth-invalid');
      throw new AuthWriteError('Your session has expired. Please sign in again.', 401);
    }

    if (!response.ok) {
      const message = await getResponseErrorMessage(response.clone()) ?? fallbackMessage;
      dispatchClientEvent('garden:sync-error', { message });
      throw new AuthWriteError(message, response.status);
    }

    return response;
  } catch (error) {
    if (error instanceof AuthWriteError) throw error;

    dispatchClientEvent('garden:sync-error', { message: fallbackMessage });
    throw new AuthWriteError(fallbackMessage);
  }
}
